// src/components/business/business/businessSubmitConfirmation.tsx

import React, { useContext, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Divider,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useAuthStatus } from "../../../util/useAuthStatus";
import useLoadReduxStoreData from "../../../redux/reducers/loadReduxStoreData";
import { BusinessQueryNames, GET_BUSINESS_CATEGORIES } from "../../../graphql/queries";
import { useSelectedBusiness } from "../../../redux/store";
import { UserContext } from "../../auth/auth-context";
import {
  getPageOptions,
  getRouteMap,
  isAFHCategoryName,
  resolveDefaultPageIdForBusiness,
} from "../../../pages/business/defaultPageDialog";
import { useTokenLoader } from "../../../util/tokenLoader";
import { useSaveDefaultPage } from "../../../hooks/useSaveDefaultPage";
import { hasStandaloneBookingAccess } from "../../../../core/business/productAccess";

interface BusinessSubmitConfirmationProps {
  open: boolean;
  businessId?: number;
  onModalClose?: () => void; 
}

const BusinessSubmitConfirmation: React.FC<BusinessSubmitConfirmationProps> = ({
  open,
  businessId, 
  onModalClose,
}) => {
  const navigate = useNavigate();
  const { providerUser } = useContext(UserContext)!;
  const selectedBusiness = useSelectedBusiness();
  const { data: categoriesData } = useQuery(GET_BUSINESS_CATEGORIES);
  const { isLoggedIn } = useAuthStatus();
  const { refetch: reloadBusinesses } = useLoadReduxStoreData(
    BusinessQueryNames.GET_MY_BUSINESSES,
    { enabled: isLoggedIn }
  );
  const { trigger: fetchToken } = useTokenLoader();
  const saveDefaultPage = useSaveDefaultPage();

  const selectedCategory = useMemo(
    () =>
      (categoriesData?.getBusinessCategories || []).find(
        (c: any) =>
          Number(c?.id) === Number(selectedBusiness?.businessBasic?.businessCategoryId)
      ),
    [categoriesData?.getBusinessCategories, selectedBusiness?.businessBasic?.businessCategoryId]
  );
  const isAFHBusiness = isAFHCategoryName(selectedCategory?.name);
  const canUseStandaloneBooking = useMemo(
    () => hasStandaloneBookingAccess(providerUser, isAFHBusiness),
    [providerUser, isAFHBusiness]
  );
  const allowedPageOptions = useMemo(
    () => getPageOptions(isAFHBusiness, canUseStandaloneBooking),
    [isAFHBusiness, canUseStandaloneBooking]
  );
  const [selectedPageId, setSelectedPageId] = useState(
    resolveDefaultPageIdForBusiness(
      providerUser?.defaultPageId ?? 2,
      isAFHBusiness,
      canUseStandaloneBooking
    )
  );
  const [loading, setLoading] = useState(false);

  const businessUrl =
    selectedBusiness?.businessBasic?.url ?? "default-business";
  const staffUrl = providerUser?.url ?? "default-staff";
  const routeMap = useMemo<Record<number, string>>(
    () =>
      getRouteMap({
        businessUrl,
        staffUrl,
        isAFHBusiness,
        canUseStandaloneBooking,
      }),
    [businessUrl, staffUrl, isAFHBusiness, canUseStandaloneBooking]
  );

  React.useEffect(() => {
    setSelectedPageId((prev) =>
      resolveDefaultPageIdForBusiness(
        prev,
        isAFHBusiness,
        canUseStandaloneBooking
      )
    );
  }, [isAFHBusiness, canUseStandaloneBooking]);

  const defaultPageLabel = useMemo(
    () => allowedPageOptions.find((opt) => opt.id === selectedPageId)?.label ?? "",
    [allowedPageOptions, selectedPageId]
  );
  const selectedPageDescription = useMemo(
    () => allowedPageOptions.find((opt) => opt.id === selectedPageId)?.label ?? "",
    [allowedPageOptions, selectedPageId]
  );

  const handleChange = (e: SelectChangeEvent<string>) => {
    setSelectedPageId(parseInt(e.target.value, 10));
  };

  const handleGo = async () => {
    setLoading(true);
    try {
      // 1) refresh your JWT
      await fetchToken();
      // 2) save default page on the server
      await saveDefaultPage(selectedPageId, businessId);
      // 3) reload the user's businesses
      await reloadBusinesses();
      // 4) finally navigate
      const target = routeMap[selectedPageId] ?? routeMap[1] ?? "/";
      navigate(target); 
    } catch (err) {
      console.error(err);
      navigate("/"); 
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Business Created!</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Congratulations, {providerUser?.username || "there"}! Your business
          has been created.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography sx={{ mb: 2 }}>
          Select your default start page (your landing page at login). Change it
          anytime in your profile.
        </Typography>

        <FormControl fullWidth>
          <InputLabel id="default-page-select-label">Default Page</InputLabel>
          <Select
            labelId="default-page-select-label"
            value={String(selectedPageId)}
            label="Default Page"
            onChange={handleChange}
            disabled={loading}
          >
            {allowedPageOptions.map((opt) => (
              <MenuItem key={opt.id} value={String(opt.id)}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedPageDescription && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, fontStyle: "italic" }}
          >
            {selectedPageDescription}
          </Typography>
        )}

        <Box sx={{ mt: 2, textAlign: "center" }}>
          📌 Currently: <strong>{defaultPageLabel}</strong>
        </Box>
      </DialogContent>

      <DialogActions> 
        <Button onClick={handleGo} variant="contained" disabled={loading}>
          {loading ? "Taking you there…" : "Go"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessSubmitConfirmation;
